import React, { useMemo } from 'react';
import {
  Box,
  Select,
  HStack,
  Text,
  Icon,
  Badge,
  VStack,
  Checkbox,
  CheckboxGroup,
  Menu,
  MenuButton,
  MenuList,
  MenuDivider,
  Button,
  Wrap,
  WrapItem,
  Portal,
  Tooltip,
} from '@chakra-ui/react';
import { MdAccountBalance, MdFilterList, MdWarning, MdError } from 'react-icons/md';
import { useAccounts, AccountSummary } from '../../hooks/useAccounts';
import { useUser } from '../../Context/userContext';

function formatCurrency(amount: number | null, currency = 'USD'): string {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

type TokenStatus = 'ACTIVE' | 'LOGIN_REQUIRED' | 'REVOKED';

function statusLabel(status: TokenStatus): string {
  if (status === 'LOGIN_REQUIRED') return 'Reconnect required';
  if (status === 'REVOKED') return 'Disconnected';
  return 'Connected';
}

export interface AccountSelectorProps {
  value?: string;
  values?: string[];
  onChange?: (accountId: string) => void;
  onChangeMulti?: (accountIds: string[]) => void;
  multiSelect?: boolean;
  showAllOption?: boolean;
  allOptionLabel?: string;
  showBalances?: boolean;
  groupByBank?: boolean;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  isDisabled?: boolean;
  label?: string;
  compact?: boolean;
}

export const AccountSelector: React.FC<AccountSelectorProps> = ({
  value = '',
  values = [],
  onChange,
  onChangeMulti,
  multiSelect = false,
  showAllOption = true,
  allOptionLabel = 'All Accounts',
  showBalances = false,
  groupByBank = true,
  placeholder = 'Select account',
  size = 'md',
  isDisabled = false,
  label,
  compact = false,
}) => {
  const { accounts, isLoading } = useAccounts();
  const { plaidTokens } = useUser();

  const tokenStatusMap = useMemo(() => {
    const map = new Map<string, TokenStatus>();
    (plaidTokens ?? []).forEach((t) => map.set(t.id, t.status as TokenStatus));
    return map;
  }, [plaidTokens]);

  const getStatus = (account: AccountSummary): TokenStatus =>
    (account.plaidTokenId ? tokenStatusMap.get(account.plaidTokenId) : undefined) ?? 'ACTIVE';

  const accountsByBank = useMemo(() => {
    if (!groupByBank) return { 'All Banks': accounts };
    const grouped: Record<string, AccountSummary[]> = {};
    accounts.forEach((account) => {
      const bankName = account.institutionName || 'Unknown Bank';
      if (!grouped[bankName]) grouped[bankName] = [];
      grouped[bankName].push(account);
    });
    return grouped;
  }, [accounts, groupByBank]);

  const handleSingleChange = (e: React.ChangeEvent<HTMLSelectElement>) => onChange?.(e.target.value);

  const handleMultiChange = (selectedIds: string[] | (string | number)[]) => {
    const stringIds = selectedIds.map((id) => String(id));
    if (stringIds.length === accounts.length && accounts.length > 0) {
      onChangeMulti?.([]);
    } else {
      onChangeMulti?.(stringIds);
    }
  };

  // Inline status badge for multi/compact modes
  const StatusBadge = ({ account }: { account: AccountSummary }) => {
    const status = getStatus(account);
    if (status === 'ACTIVE') {
      return <Badge colorScheme="green" fontSize="2xs" px={1.5} borderRadius="full">Connected</Badge>;
    }
    return (
      <Tooltip label={statusLabel(status)} fontSize="xs">
        <Badge
          colorScheme={status === 'REVOKED' ? 'red' : 'orange'}
          fontSize="2xs"
          px={1.5}
          borderRadius="full"
          display="flex"
          alignItems="center"
          gap={0.5}
        >
          <Icon as={status === 'REVOKED' ? MdError : MdWarning} boxSize={2.5} />
          {statusLabel(status)}
        </Badge>
      </Tooltip>
    );
  };

  // ── Compact multi-select ─────────────────────────────────────────────────────
  if (compact && multiSelect) {
    const selectedCount = values.length;
    const totalCount = accounts.length;
    const hasIssues = accounts.some((a) => getStatus(a) !== 'ACTIVE');

    return (
      <Menu autoSelect={false}>
        <MenuButton
          as={Button}
          leftIcon={<Icon as={hasIssues ? MdWarning : MdFilterList} color={hasIssues ? 'orange.400' : undefined} />}
          variant="outline"
          size={size}
          isDisabled={isDisabled || isLoading}
          borderColor={hasIssues ? 'orange.400' : 'xacto.mutedNavy'}
          color="text.secondary"
          _hover={{ bg: 'bg.muted', color: 'text.primary' }}
        >
          {selectedCount === 0 ? allOptionLabel : 'Accounts'}
          {selectedCount > 0 && selectedCount < totalCount && (
            <Badge ml={2} colorScheme="cyan" borderRadius="full">{selectedCount}</Badge>
          )}
        </MenuButton>
        <Portal>
          <MenuList maxW="460px" zIndex={9999} px={2} py={2}>
            <HStack justify="space-between" px={2} pb={2}>
              <Text fontSize="sm" fontWeight="medium" color="text.primary">Filter by Account</Text>
              {values.length > 0 && (
                <Button size="xs" variant="ghost" onClick={() => onChangeMulti?.([])} color="text.muted">
                  Show All
                </Button>
              )}
            </HStack>
            <MenuDivider m={1} />
            <Box maxH="500px" overflowY="auto">
              {showAllOption && (
                <>
                  <Box p={1.5} _hover={{ bg: 'bg.muted', borderRadius: 'md' }} cursor="pointer" onClick={() => onChangeMulti?.([])}>
                    <Checkbox isChecked={values.length === 0} colorScheme="cyan" size="sm" onChange={() => onChangeMulti?.([])} pointerEvents="none">
                      <Text fontSize="sm" color="text.primary" fontWeight="medium">{allOptionLabel}</Text>
                    </Checkbox>
                  </Box>
                  <MenuDivider m={1} />
                </>
              )}
              <CheckboxGroup value={values} onChange={handleMultiChange as any}>
                <VStack align="stretch" spacing={1} px={1}>
                  {accounts.map((account) => {
                    const st = getStatus(account);
                    const rowBg = st === 'ACTIVE' ? 'green.50' : st === 'LOGIN_REQUIRED' ? 'orange.50' : 'red.50';
                    const rowBgDark = st === 'ACTIVE' ? 'green.900' : st === 'LOGIN_REQUIRED' ? 'orange.900' : 'red.900';
                    return (
                    <Box key={account.id} p={1.5} borderRadius="md"
                      bg={rowBg}
                      _dark={{ bg: rowBgDark }}
                      _hover={{ opacity: 0.85 }}>
                      <Checkbox value={account.id} colorScheme="cyan" size="sm" w="100%">
                        <HStack spacing={2} w="100%" flexWrap="wrap">
                          <Text fontSize="sm" color="text.primary">{account.name}</Text>
                          {account.institutionName && (
                            <Text fontSize="xs" color="text.muted">{account.institutionName}</Text>
                          )}
                          {account.mask && (
                            <Text fontSize="xs" color="text.muted">****{account.mask}</Text>
                          )}
                          <StatusBadge account={account} />
                        </HStack>
                      </Checkbox>
                    </Box>
                    );
                  })}
                </VStack>
              </CheckboxGroup>
            </Box>
          </MenuList>
        </Portal>
      </Menu>
    );
  }

  // ── Multi-select with checkboxes ─────────────────────────────────────────────
  if (multiSelect) {
    return (
      <Box>
        {label && (
          <HStack spacing={2} mb={2}>
            <Icon as={MdAccountBalance} color="accent.primary" />
            <Text color="text.secondary" fontSize="sm">{label}</Text>
          </HStack>
        )}
        <CheckboxGroup value={values} onChange={handleMultiChange as any}>
          <Wrap spacing={2}>
            {accounts.map((account) => (
              <WrapItem key={account.id}>
                <Checkbox value={account.id} colorScheme="cyan" size={size} isDisabled={isDisabled}>
                  <HStack spacing={1}>
                    <Text fontSize="sm">{account.name}</Text>
                    {account.institutionName && (
                      <Text fontSize="xs" color="text.muted">{account.institutionName}</Text>
                    )}
                    {account.mask && (
                      <Text fontSize="xs" color="text.muted">****{account.mask}</Text>
                    )}
                    {showBalances && account.currentBalance !== null && (
                      <Badge colorScheme="gray" fontSize="xs">{formatCurrency(account.currentBalance)}</Badge>
                    )}
                    <StatusBadge account={account} />
                  </HStack>
                </Checkbox>
              </WrapItem>
            ))}
          </Wrap>
        </CheckboxGroup>
      </Box>
    );
  }

  // ── Single select dropdown ───────────────────────────────────────────────────
  // <option> only supports text, so we embed the status in the label text
  return (
    <Box>
      {label && (
        <HStack spacing={2} mb={2}>
          <Icon as={MdAccountBalance} color="accent.primary" />
          <Text color="text.secondary" fontSize="sm">{label}</Text>
        </HStack>
      )}
      <Select
        value={value}
        onChange={handleSingleChange}
        placeholder={isLoading ? 'Loading accounts...' : placeholder}
        isDisabled={isDisabled || isLoading}
        bg="bg.muted"
        borderColor="xacto.mutedNavy"
        color="text.primary"
        _hover={{ borderColor: 'accent.primary' }}
        size={size}
      >
        {showAllOption && (
          <option value="" style={{ background: '#1a1f36' }}>{allOptionLabel}</option>
        )}
        {groupByBank ? (
          Object.entries(accountsByBank).map(([bankName, bankAccounts]) => (
            <optgroup key={bankName} label={bankName}>
              {bankAccounts.map((account) => {
                const s = getStatus(account);
                const statusText = s !== 'ACTIVE' ? ` — ${statusLabel(s)}` : ' — Connected';
                return (
                  <option key={account.id} value={account.id} style={{ background: '#1a1f36' }}>
                    {account.name}
                    {account.mask ? ` (****${account.mask})` : ''}
                    {showBalances && account.currentBalance !== null ? ` - ${formatCurrency(account.currentBalance)}` : ''}
                    {statusText}
                  </option>
                );
              })}
            </optgroup>
          ))
        ) : (
          accounts.map((account) => {
            const s = getStatus(account);
            const statusText = s !== 'ACTIVE' ? ` — ${statusLabel(s)}` : ' — Connected';
            return (
              <option key={account.id} value={account.id} style={{ background: '#1a1f36' }}>
                {account.name}
                {account.mask ? ` (****${account.mask})` : ''}
                {showBalances && account.currentBalance !== null ? ` - ${formatCurrency(account.currentBalance)}` : ''}
                {statusText}
              </option>
            );
          })
        )}
      </Select>
    </Box>
  );
};

export default AccountSelector;
